import { MOCK } from '../mock/flag';
import { DELETE_WAIT_MS, settledWithin } from './db';
import { identityUser, onIdentityChange } from './identity';
import { read, write, removePrefix, deleteRelayData, deleteOtherRelayData } from './relay-db';
import { POST_MS, QUERY_MS, SEND_MS } from './relay-serve';
import { BudgetSpent } from './relay-budget';
import { checkScope, errorMessage as message, grantRecords, granting, kindOf, liveGrants, MAX_GRANTS, putGrant, refusal, scopeOf, requestCode, REQUEST_TTL_MS, reconcileMirror, seenKey, sync, type GrantRequest, type HelperGrant, type RelayCategory, type RelayConnection, type RelayGrant, type RelayQuery } from './relay-grants';
import { parseLocator, parseSubject, readKey, sameSubject, type SubjectKey } from './relaySubject';
import type { RefLocator } from '../subject';
import { isTopic, RANGE_TASK, type TaskBody } from './relayTask';
export { RELAY_CATEGORIES, type GrantRequest, type Scope, type HelperGrant, type RelayCategory, type RelayConnection, type RelayGrant, type RelayQuery, type RelayTool } from './relay-grants';
export { sameSubject, type SubjectKey } from './relaySubject';
/** A request started in Changeling, waiting for a new agent: `req` is `XXXX-XXXX`. */
export interface PendingRequest { req: string; subject: SubjectKey; scope: RelayCategory[]; created: number }
/** What a pairing link carries, and what this browser grants with it. */
export interface PairOptions { peer: string; route: string; code: string; relay?: string; subject: SubjectKey; scope: RelayCategory[]; req?: string }
export type RelayPresence = 'listening' | 'wakeable' | 'channel' | 'connected' | 'offline';
/**
 * A review task: `topic`, what it is about in this browser (a review
 * thread, or the subject's conversation), under which the panel finds its
 * tasks; `kind` and `payload` what was sent (relayTask.ts), though the
 * helper gives back only the payload's `head` and `instruction`.
 */
export interface RelayTask { id: string; topic: string; kind: TaskBody['kind']; status: 'unconfirmed' | 'queued' | 'working' | 'completed' | 'failed' | 'cancelled'; result: string | null; payload: { head?: string; [key: string]: unknown }; lease_until: number | null;
  /** What the person asked with it (its `instruction`): as the helper gives it back, else as this browser recorded sending it (`asked`). */
  asked?: string }
interface Pending { connection: string; task: RelayTask }

/**
 * The iroh relay hosts this deployment's Content-Security-Policy lets the
 * relay worker reach (`RELAY_HOSTS` in crates/server/src/headers.rs, which
 * relay.test.ts compares with this list): n0's public relays, without the
 * trailing dot iroh writes them with.
 */
export const RELAY_HOSTS: readonly string[] = ['*.relay.n0.iroh.link'];

/**
 * Whether a pairing link's relay URL is one this browser can reach: https on
 * the default port, on a host of `RELAY_HOSTS`. A trailing dot on the host
 * (iroh's spelling) is accepted; the worker dials the host without it.
 */
export function isAllowedRelay(url: string): boolean {
  let parsed: URL;
  try { parsed = new URL(url); } catch { return false; }
  if (parsed.protocol !== 'https:' || parsed.port || parsed.username || parsed.password) return false;
  const host = parsed.hostname.toLowerCase().replace(/\.$/, '');
  return RELAY_HOSTS.some(allowed => allowed.startsWith('*.')
    ? host.endsWith(allowed.slice(1)) && host.length > allowed.length - 1
    : host === allowed);
}

/** An endpoint ID as iroh writes it, and as a pairing link's `peer=` carries it: 64 lowercase hex digits. */
export const ENDPOINT_ID = /^[0-9a-f]{64}$/;
/**
 * A fingerprint of an endpoint ID for people to compare: its first 20 hex
 * digits (80 bits, past any search for a key with a chosen prefix), in
 * groups of four, as `xxxx-xxxx-xxxx-xxxx-xxxx`. The plugin derives the
 * same (plugins/changeling/scripts/fingerprint.mjs); the wire contract's
 * `fingerprints` pin both. Throws for anything but an endpoint ID.
 */
export function fingerprint(peerId: string): string {
  if (!ENDPOINT_ID.test(peerId)) throw new Error('Invalid endpoint ID');
  return peerId.slice(0, 20).match(/.{4}/g)!.join('-');
}

/** What `showFingerprint` shows for a stored ID that is not an endpoint ID. */
export const INVALID_FINGERPRINT = 'invalid endpoint ID';
/** `fingerprint` for a page to show: a stored ID that is not an endpoint ID (damaged storage) shows as such instead of throwing. */
export function showFingerprint(peerId: string): string {
  return ENDPOINT_ID.test(peerId) ? fingerprint(peerId) : INVALID_FINGERPRINT;
}

/** The signed-in user, once the session knows it. */
async function currentUser(): Promise<string> {
  let user = identityUser();
  if (user === undefined) {
    user = await new Promise<string | null>(resolve => {
      const off = onIdentityChange(() => { const u = identityUser(); if (u !== undefined) { off(); resolve(u); } });
    });
  }
  if (!user) throw new Error('Sign in to use local agents.');
  return user;
}

// Relay data belongs to the signed-in user alone: when the session names a
// user, every other user's is deleted, and all of it when nobody is signed
// in, as db.ts does with the app's other data.
function forgetOtherUsers() {
  const user = identityUser();
  if (user !== undefined && !MOCK) void deleteOtherRelayData(user).catch(() => {});
}
onIdentityChange(forgetOtherUsers);
forgetOtherUsers();

let worker: SharedWorker | undefined;
const requests = new Map<string, { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }>();
/** What a request to a worker that ended under it fails with. */
export const WORKER_ENDED = 'The encrypted agent connection restarted. Retry to check the outcome.';
/** How long a worker has to answer a ping, after a request it never acknowledged, before it is taken for gone. */
export const PING_MS = 2000;
/** The first restart's wait; each one after waits twice as long. */
export const RESTART_MS = 1000;
/** Restarts in a row, none answering a request, before this tab stops restarting the worker. */
export const MAX_RESTARTS = 3;
/** Why this tab no longer starts the worker by itself (`relay.workerDown`). */
export const WORKER_DOWN = 'The encrypted agent connection keeps stopping in this browser. Reload the page, or try again.';
/** What `relay.revive` fails with when the worker it started ended before it answered. */
export const REVIVE_FAILED = 'It stopped again as it started. Reload the page, or try again later.';
/** How long a worker must stay up after it is attached for its failures to stop counting as in a row. */
export const STABLE_MS = 60_000;
/** Restarts in a row: since a worker last answered a request, or stayed up STABLE_MS once attached. */
let restarts = 0;
let stableTimer: ReturnType<typeof setTimeout> | undefined;
/** The restart waiting out its backoff. */
let restartTimer: ReturnType<typeof setTimeout> | undefined;
let down: string | null = null;
const downListeners = new Set<(why: string | null) => void>();
function setDown(why: string | null) {
  if (down === why) return;
  down = why;
  for (const cb of downListeners) cb(why);
}
/**
 * Forgets `w`, a worker that could not start, said it is ending (a panic in
 * its module closes it, which fires no error here), or answers nothing: what
 * it was asked fails with `why`, and the tab's listeners attach to a new one
 * after RESTART_MS, twice as long each time in a row. A failure that comes
 * back every time (each start binds the user's endpoint again) is not
 * restarted past MAX_RESTARTS: the tab says the worker is down, and every
 * request fails with WORKER_DOWN without starting one, until the person's
 * Try again (`relay.revive`) does and it answers.
 */
function dropWorker(w: SharedWorker, why: string) {
  if (worker !== w) return;
  // A worker that only went quiet may come back, and `new SharedWorker` would
  // then return this same one: this port leaves it, so no query waits on it.
  try { w.port.postMessage({ op: 'detach' }); w.port.close(); } catch { /* already gone */ }
  clearTimeout(stableTimer);
  for (const p of requests.values()) { clearTimeout(p.timer); p.reject(new Error(why)); }
  requests.clear(); worker = undefined; attachedAs = undefined; probing = false;
  if (restarts >= MAX_RESTARTS) { setDown(WORKER_DOWN); return; }
  restartTimer = setTimeout(() => { restartTimer = undefined; announce(); }, RESTART_MS * 2 ** restarts++);
}
/**
 * The relay worker's port, started when first needed. While the worker is
 * down only `reviving` (the person's Try again) starts one: the page's own
 * polls and writes fail with WORKER_DOWN, so they can't restart a worker
 * that keeps ending past MAX_RESTARTS.
 */
function workerPort(reviving = false): MessagePort {
  if (MOCK) throw new Error('Local agents are unavailable in the demo.');
  if (!globalThis.SharedWorker) throw new Error('This browser does not support shared agent connections. Use a browser with SharedWorker support.');
  if (down !== null && !reviving) throw new Error(WORKER_DOWN);
  if (!worker) {
    // Started by a request while a restart waited: no announce is on its
    // way, so this tab's listeners attach here. Once down, they attach when
    // the revived worker first answers.
    const restarting = restartTimer !== undefined;
    clearTimeout(restartTimer); restartTimer = undefined;
    const w = worker = new SharedWorker(new URL('./relay-worker.ts', import.meta.url), { type: 'module', name: 'remote-relay-v1' });
    w.port.onmessage = ({ data }) => {
      if (worker !== w) return;
      if (data.event === 'crashed') { dropWorker(w, WORKER_ENDED); return; }
      if (data.event) { void event(data); return; }
      const pending = requests.get(data.id); if (!pending) return;
      // It answers requests: a failure after this is a new one, and a worker revived is up.
      restarts = 0;
      if (down !== null) { setDown(null); announce(); }
      requests.delete(data.id); clearTimeout(pending.timer);
      if (data.error) pending.reject(Object.assign(new Error(message(data.error)), { code: data.code })); else pending.resolve(data.result);
    };
    w.onerror = () => dropWorker(w, 'Encrypted agent connection could not start');
    w.port.start();
    if (restarting) announce();
  }
  return worker.port;
}
let probing = false;
/** After a request `w` never acknowledged: drops `w` unless it answers a ping within PING_MS. */
function probe(w: SharedWorker) {
  if (worker !== w || probing) return;
  probing = true;
  const id = crypto.randomUUID();
  const done = () => { if (worker === w) probing = false; };
  const timer = setTimeout(() => { requests.delete(id); dropWorker(w, WORKER_ENDED); }, PING_MS);
  requests.set(id, { resolve: done, reject: done, timer });
  w.port.postMessage({ op: 'ping', id });
}
function post<T>(body: Record<string, unknown>, reviving = false): Promise<T> {
  let port: MessagePort;
  try { port = workerPort(reviving); } catch (e) { return Promise.reject(e); }
  const w = worker!;
  return new Promise<T>((resolve, reject) => {
    const id = crypto.randomUUID();
    const timer = setTimeout(() => {
      requests.delete(id);
      reject(new Error('Agent did not acknowledge the request. Retry to check its outcome.'));
      // The agent may be slow; the worker may also be gone without a word.
      probe(w);
    }, 30000);
    requests.set(id, { resolve: v => resolve(v as T), reject, timer });
    port.postMessage({ ...body, id });
  });
}
/** `subject` checked as a subject's key, which every kind of subject may be granted on. */
function grantable(subject: SubjectKey): SubjectKey {
  if (!parseSubject(subject)) throw new Error('Not a subject.');
  return subject;
}
/** Why this browser may not reach an agent through `relay`, or null when it may. */
function relayRefusal(relay?: string): Error | null {
  return relay !== undefined && !isAllowedRelay(relay) ? new Error(`This Changeling connects to agents only through the relays it allows (${RELAY_HOSTS.join(', ')}).`) : null;
}
function request<T>(user: string, peer: string, message: unknown, relay?: string): Promise<T> {
  const refused = relayRefusal(relay);
  if (refused) return Promise.reject(refused);
  return post<T>({ op: 'request', user, peer, relay, message });
}
async function connections(user: string) { return await read<RelayConnection[]>(user, 'connections') ?? []; }
async function connection(user: string, id: string) { const c = (await connections(user)).find(c => c.id === id); if (!c) throw new Error('Connect this browser to your agent first'); return c; }
async function call<T>(user: string, id: string, message: Record<string, unknown>) { const c = await connection(user, id); return request<T>(user, c.peer, { ...message, route: c.route }, c.relay); }
// Browser writes are serialized across tabs as well as within one tab, and
// are refused once the signed-in account has changed.
async function update<T>(user: string, fn: () => Promise<T>) {
  return navigator.locks.request(`remote-relay-state:${user}`, () => {
    if (identityUser() !== user) throw new Error('The signed-in account changed; reload this page.');
    return fn();
  });
}
/**
 * The unconfirmed requests. One kept before tasks had a kind and a topic
 * naming their subject (relayTask.ts `isTopic`) is dropped: the helper
 * would refuse it, and its topic can't say which repository it was about.
 */
async function outbox(user: string) { return (await read<Pending[]>(user, 'outbox') ?? []).filter(p => typeof p?.task?.kind === 'string' && isTopic(p.task.topic)); }
/**
 * Which agent this browser has sent which topic (a review thread, or a
 * subject's conversation) to, so a panel asks only those agents (and the
 * ones granted the subject) about it, whatever became of their grant
 * since: asking an agent about a topic tells it what the person is looking
 * at. Its own key in the relay database, read by every tab; dropped with
 * the pairing. A record kept before topics named their subject (`pull/7/…`,
 * under `subject` or `topic`) is dropped as it is read: it can't say which
 * repository it was about, so it would open the panel of every pull
 * request with that number.
 */
interface Sent { connection: string; topic: string }
/** The records kept, newest last. */
const SENT_MAX = 1000;
async function sentRecords(user: string): Promise<Sent[]> {
  return (await read<Sent[]>(user, 'sent') ?? []).filter(r => typeof r?.connection === 'string' && isTopic(r.topic));
}
/**
 * What the person asked of an agent on a topic, by task: kept here as it
 * is sent, for a helper that answers `tasks` with only a payload's head. A
 * panel shows it as the person's side of the conversation. Under
 * `asked:<connection>:<topic>`, as many as the helper lists (its last 10).
 */
interface Asked { task: string; text: string }
const ASKED_MAX = 10;
const askedKey = (id: string, topic: string) => `asked:${id}:${topic}`;
/**
 * The range tasks this browser sent a pairing, each with the topic,
 * subject and lines it was sent with (`SentRange`), so a result is kept as
 * a note only for a task this browser sent, on the lines it sent, whatever
 * the agent's computer says of it (state/agentNotes.ts). Under
 * `ranges:<connection>`, newest last, more than the helper keeps tasks;
 * dropped with the pairing.
 */
export interface SentRange { task: string; topic: string; subject: SubjectKey; path: string; side: 'old' | 'new'; blob: string; start_line: number; end_line: number; commit: string }
const rangesKey = (id: string) => `ranges:${id}`;
const RANGES_MAX = 1000;
/**
 * Where this browser's reading of a pairing's results (`relay.results`)
 * has got to: the helper's cursor past the last result kept or refused for
 * good. Under `results:<connection>`; dropped with the pairing.
 */
const cursorKey = (id: string) => `results:${id}`;
/** A results cursor's order: `<updated>:<id>`, by time and then ID (the helper's task.mjs). */
export function laterCursor(a: string, b: string | null): boolean {
  if (b === null) return true;
  const [at, ai] = [Number(a.slice(0, a.indexOf(':'))), a.slice(a.indexOf(':') + 1)];
  const [bt, bi] = [Number(b.slice(0, b.indexOf(':'))), b.slice(b.indexOf(':') + 1)];
  return at > bt || (at === bt && ai > bi);
}
/** One finished range task's result, as `results` pages them. */
export interface RelayResult { id: string; topic: string; result: string; updated: number }
/** The listeners for a serve's word of a pairing's newest result (`relay.onResults`). */
const resultListeners = new Set<(connection: string, after: string) => void>();
/**
 * Cached task history. Kept under `history:<connection>:<topic>`; what was
 * kept under a topic from before topics named their subject is never read
 * again (no topic is written so now), and goes with its pairing.
 */
async function history(user: string, key: string): Promise<RelayTask[]> {
  return await read<RelayTask[]>(user, key) ?? [];
}
/**
 * `tasks` with what was asked with each: as the helper gives it back (its
 * `instruction`, the person's own text), else as this browser recorded it,
 * for a helper that gives back only the head.
 */
async function withAsked(user: string, id: string, topic: string | undefined, tasks: RelayTask[]): Promise<RelayTask[]> {
  const asked = new Map(topic === undefined ? [] : (await read<Asked[]>(user, askedKey(id, topic)) ?? []).map((a) => [a.task, a.text]));
  return tasks.map((t) => {
    const text = typeof t.payload?.instruction === 'string' && t.payload.instruction ? t.payload.instruction : asked.get(t.id);
    return text === undefined ? t : { ...t, asked: text };
  });
}
async function removeLocally(user: string, id: string) {
  await update(user, async () => {
    await write(user, 'connections', (await connections(user)).filter(c => c.id !== id));
    await removePrefix(user, `history:${id}:`);
    await removePrefix(user, `asked:${id}:`);
    await write(user, rangesKey(id), undefined);
    await write(user, cursorKey(id), undefined);
    await write(user, 'outbox', (await outbox(user)).filter(p => p.connection !== id));
    await write(user, 'grants', (await grantRecords(user)).filter(g => g.connection !== id));
    await write(user, 'sent', (await sentRecords(user)).filter(r => r.connection !== id));
    await write(user, seenKey(id), undefined);
  });
  changed(user);
}
async function deliver(id: string, task: RelayTask) {
  const user = await currentUser();
  let result: RelayTask;
  try { result = await call<RelayTask>(user, id, { op: 'send', id: task.id, topic: task.topic, kind: task.kind, payload: task.payload }); }
  catch(e) {
    if ((e as { code?: string }).code === 'rejected') await update(user, async () => write(user, 'outbox', (await outbox(user)).filter(p => p.task.id !== task.id)));
    throw e;
  }
  const key = `history:${id}:${task.topic}`;
  await update(user, async () => {
    if ((await connections(user)).some(c => c.id === id)) await write(user, key, [...(await history(user, key)).filter(t => t.id !== task.id), result].slice(-10));
  });
  await update(user, async () => write(user, 'outbox', (await outbox(user)).filter(p => p.task.id !== task.id)));
  return result;
}

/** Removes a connection, its task history and its unconfirmed requests from
 *  this browser only. The agent's computer is not contacted and keeps its
 *  grant until it is revoked there. */
export async function forget(connectionId: string): Promise<void> {
  await removeLocally(await currentUser(), connectionId);
}

/** Deletes this browser's relay identity key and every connection, task
 *  history and unconfirmed request of the signed-in user. Agents keep their
 *  grants to the old identity, which no longer exists here; connecting again
 *  needs a new connection link. */
export async function resetIdentity(): Promise<void> {
  const user = await currentUser();
  await closeAndDelete(user, 'reset');
  // The worker detached this tab when it stopped the old identity.
  announce();
}

/** Deletes the signed-in user's relay data, identity included (sign-out).
 *  Never rejects, and resolves after `DELETE_WAIT_MS` at most; a deletion
 *  that another context holds up finishes in the background. */
export async function clearForSignOut(): Promise<void> {
  const user = identityUser();
  if (!user) return;
  // This tab never attaches as `user` again: it leaves for the sign-in page.
  leaving.add(user);
  await settledWithin(closeAndDelete(user, 'signout'), DELETE_WAIT_MS);
}

/** The lock held from closing a user's endpoint until their data is deleted, which a tab waits for before attaching again. */
const resetLock = (user: string) => `remote-relay-reset:${user}`;
/** Closes the user's endpoint in the worker, then deletes their relay data, holding `resetLock`. */
function closeAndDelete(user: string, reason: 'reset' | 'signout'): Promise<void> {
  return navigator.locks.request(resetLock(user), async () => {
    if (worker) await post({ op: 'close', user, reason }).catch(() => {});
    await deleteRelayData(user);
  });
}

// Serving the subject tools (relay-serve.ts). A tab attaches to the worker while it
// listens for grant requests or grant changes, or answers queries, and
// isn't hidden or frozen; the worker serves a user only while one of their
// tabs is attached.
const grantListeners = new Set<() => void>();
const seenListeners = new Set<(connection: string, at: number) => void>();
const requestListeners = new Set<(r: GrantRequest) => void>();
const goneListeners = new Set<(id: string, connection: string) => void>();
/**
 * What an answerer is given with a query: `ms`, the time it has before its
 * answer is too late; `signal`, which aborts when the worker passed the query
 * on to another tab; `spend`, to ask the worker before each GraphQL page it
 * reads (rejecting with BudgetSpent when the worker's budget says no).
 */
export interface AnswerCall { ms: number; signal: AbortSignal; spend: () => Promise<void> }
export type Answerer = (q: RelayQuery, call: AnswerCall) => Promise<unknown>;
/** The queries this tab is answering, by the worker's id, to cancel. */
const cancels = new Map<string, AbortController>();
const answerers = new Set<Answerer>();
/** The user this tab last attached as, so a detach is sent only when needed. */
let attachedAs: string | undefined;
let announcing = 0;
/** The users this tab signed out: it doesn't attach as them again. */
const leaving = new Set<string>();
/** The page is in the back/forward cache, unloading, or frozen. */
let hidden = false;
/** Tells the worker whether, and as whom, this tab is attached and answers queries. */
function announce() {
  const epoch = ++announcing;
  const wanted = grantListeners.size + requestListeners.size + goneListeners.size + answerers.size > 0 && !hidden;
  if (!wanted || !identityUser()) {
    if (attachedAs !== undefined) { attachedAs = undefined; worker?.port.postMessage({ op: 'detach' }); }
    if (!wanted) return;
  }
  // Not while the user's data is being deleted (a reset or sign-out, maybe
  // in another tab): attaching then would read it back into being.
  void currentUser().then(user => navigator.locks.request(resetLock(user), async () => {
    if (epoch !== announcing || leaving.has(user) || identityUser() !== user) return;
    // A restart waits out its backoff, and a worker that is down waits to be asked.
    if (restartTimer !== undefined || down) return;
    attachedAs = user;
    workerPort().postMessage({ op: 'attach', user, serving: answerers.size > 0 });
    // Up and attached for STABLE_MS: a failure after that is not one in a row.
    const w = worker;
    clearTimeout(stableTimer);
    stableTimer = setTimeout(() => { if (worker === w) restarts = 0; }, STABLE_MS);
  })).catch(() => {});
}
onIdentityChange(() => {
  // Someone else signing in here may attach again.
  const user = identityUser();
  if (user && !leaving.has(user)) leaving.clear();
  announce();
});
if (typeof window !== 'undefined') {
  const away = () => { hidden = true; announce(); };
  const back = () => { hidden = false; announce(); };
  window.addEventListener('pagehide', away);
  // Grants may have changed while the page was in the back/forward cache.
  window.addEventListener('pageshow', e => { back(); if (e.persisted) for (const cb of grantListeners) cb(); });
  document.addEventListener('freeze', away);
  document.addEventListener('resume', back);
}
function listen<T>(set: Set<T>, item: T): () => void {
  set.add(item); announce();
  return () => { if (set.delete(item)) announce(); };
}
/** Tells this tab's listeners and, through the worker, every other tab's that grants changed. */
function changed(user: string) {
  for (const cb of grantListeners) cb();
  try { workerPort().postMessage({ op: 'changed', user }); } catch { /* no worker: nothing else to tell */ }
}
async function event(data: { event: string; user: string; reason?: string; request?: GrantRequest; id?: string; connection?: string; qid?: string; query?: RelayQuery; ms?: number; at?: number; after?: string }) {
  const mine = data.user === identityUser();
  if (data.event === 'cancel') { cancels.get(data.qid!)?.abort(new Error('Another Changeling tab is answering this.')); return; }
  if (data.event === 'query') {
    const answer = [...answerers].at(-1);
    // Another account's query, or none to answer with: the worker asks another tab now.
    if (!answer || !mine) { worker?.port.postMessage({ op: 'answered', qid: data.qid, skip: true }); return; }
    const query = data.query!;
    // Taken: the worker hands it to no other tab now, and waits for this one's answer.
    worker?.port.postMessage({ op: 'received', qid: data.qid });
    const ms = typeof data.ms === 'number' && Number.isFinite(data.ms) ? data.ms : QUERY_MS - SEND_MS - POST_MS;
    const qid = data.qid!, cancel = new AbortController();
    cancels.set(qid, cancel);
    const spend = () => post<void>({ op: 'spend', qid }).catch((e: Error & { code?: string }) => { throw e.code === 'budget' ? new BudgetSpent(e.message) : e; });
    let reply: { result: unknown } | { error: string };
    try {
      // The worker checked too; this tab checks the grants as they are now.
      const why = refusal(await liveGrants(data.user), query.connection, query.tool, query.subject);
      if (why) throw new Error(why);
      reply = { result: await answer(query, { ms, signal: cancel.signal, spend }) };
    } catch (e) { reply = { error: message(e) }; }
    finally { cancels.delete(qid); }
    worker?.port.postMessage({ op: 'answered', qid, ...reply });
    return;
  }
  if (!mine) return;
  // The worker stopped this user and detached this tab. After an identity
  // reset (maybe in another tab) it attaches again, once the old data is
  // deleted and if still signed in as that user (`announce` sees to both);
  // after a sign-out it doesn't (it attaches when someone signs in).
  if (data.event === 'stopped') {
    attachedAs = undefined;
    if (data.reason === 'reset') announce();
  }
  else if (data.event === 'grants') for (const cb of grantListeners) cb();
  else if (data.event === 'grant-request') for (const cb of requestListeners) cb(data.request!);
  else if (data.event === 'grant-request-gone') for (const cb of goneListeners) cb(data.id!, data.connection!);
  else if (data.event === 'seen' && typeof data.at === 'number') for (const cb of seenListeners) cb(data.connection!, data.at);
  else if (data.event === 'results' && typeof data.after === 'string') for (const cb of resultListeners) cb(data.connection!, data.after);
}
/** Sends `connection`'s unacknowledged grant changes now, while `user` is signed in here; the worker resends what fails. */
async function syncNow(user: string, connection: string) {
  const guard = () => { if (identityUser() !== user) throw new Error('The signed-in account changed.'); };
  try { if (await sync(user, connection, msg => call(user, connection, msg), { guard, origin: location.origin })) changed(user); } catch { /* resent by the worker */ }
}
/** Brings the grants in line with a helper reply's mirror (`reconcileMirror`), and sends what that queues. */
async function reconcile(user: string, connection: string, reply: unknown) {
  if (await update(user, () => reconcileMirror(user, connection, reply))) { changed(user); void syncNow(user, connection); }
}

const BASE32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
/** The unexpired requests started in Changeling (one kept before subjects, naming no subject, is dropped: it expires in minutes anyway). */
async function pendingRequests(user: string): Promise<PendingRequest[]> {
  const now = Date.now();
  return (await read<PendingRequest[]>(user, 'requests') ?? []).filter(r => now - r.created < REQUEST_TTL_MS && readKey(r.subject) !== null);
}

const PEER = ENDPOINT_ID, CODE = /^[A-Za-z0-9_-]{1,100}$/;
/**
 * A pairing link's parameters (as `takeInvite` returns them, URI-encoded),
 * or null when one is missing or malformed. The relay URL must be https;
 * whether this deployment allows it is `isAllowedRelay`'s to say.
 */
/**
 * A pairing link's parameters: the agent, its invitation, and what it asks
 * for, a subject (its key) or a history's locator (`locator`, which this
 * browser resolves before the person approves it: relayAgents.svelte.ts).
 */
export type Invite = { peer: string; route: string; code: string; relay?: string; req?: string } & ({ subject: SubjectKey; locator?: undefined } | { locator: RefLocator; subject?: undefined });
export function parseInvite(params: string): Invite | null {
  try {
    const p = new URLSearchParams(decodeURIComponent(params));
    const peer = p.get('peer'), route = p.get('route'), code = p.get('code'), subject = readKey(p.get('subject')), locator = p.has('subject') ? null : parseLocator(p.get('locator'));
    if (!peer || !route || !code || (!subject && !locator) || !PEER.test(peer) || !/^[0-9a-f]{32}$/i.test(route) || !CODE.test(code)) return null;
    const relay = p.get('relay') ?? undefined;
    if (relay !== undefined && (relay.length > 2048 || new URL(relay).protocol !== 'https:')) return null;
    const rawReq = p.get('req');
    const req = rawReq === null ? undefined : requestCode(rawReq);
    if (req === null) return null;
    return { peer, route, code, ...(relay === undefined ? {} : { relay }), ...(subject ? { subject } : { locator: locator! }), ...(req === undefined ? {} : { req }) };
  } catch { return null; }
}

export const relay = {
  /** The pairings, and when this browser last heard from each one's agent (absent: never yet). */
  list: async (): Promise<{ connections: RelayConnection[]; seen: Record<string, number> }> => {
    const user = await currentUser();
    const list = await connections(user);
    const seen: Record<string, number> = {};
    for (const c of list) { const at = await read<number>(user, seenKey(c.id)); if (typeof at === 'number') seen[c.id] = at; }
    return { connections: list, seen };
  },
  /** Pairs with the agent of a link and grants it `subject` with `scope`. */
  pair: async ({ peer, route, code, relay, subject, scope, req }: PairOptions): Promise<RelayConnection> => {
    const user = await currentUser();
    const target = grantable(subject), granted = checkScope(scope, kindOf(target));
    const code8 = req === undefined ? undefined : requestCode(req);
    if (code8 === null) throw new Error('Not a request code.');
    const refused = relayRefusal(relay);
    if (refused) throw refused;
    const id = `${peer}/${route}`;
    // The grant the pairing makes is recorded first, so no context that
    // sees the helper's mirror meanwhile (pairing again with an agent
    // already paired) takes it for one to revoke.
    const before = await update(user, async () => {
      if ((await connections(user)).some(c => c.id === id) && (await liveGrants(user)).filter(g => g.connection === id && !sameSubject(g.subject, target)).length >= MAX_GRANTS)
        throw new Error(`This agent can read at most ${MAX_GRANTS} subjects. Revoke one before connecting it to another.`);
      const old = (await grantRecords(user)).find(g => g.connection === id && sameSubject(g.subject, target));
      await putGrant(user, id, target, granting(granted, true));
      return { old, made: (await grantRecords(user)).find(g => g.connection === id && sameSubject(g.subject, target))! };
    });
    let reply: { name: string; harness: string; grants?: HelperGrant[] };
    try { reply = await request(user, peer, { op: 'pair', route, code, subject: target, scope: granted, origin: location.origin, ...(code8 ? { req: code8.replace('-', '') } : {}) }, relay); }
    catch (e) {
      // Not paired. Untouched, the record is put back as it was. Still this
      // pairing's grant but touched, another context may have sent it
      // meanwhile (reconciling the mirror of an agent already paired), so
      // the helper may hold it: it is undone there too, back to the earlier
      // scope or revoked, unless the earlier grant had this very scope.
      // Changed to anything else (another tab revoked it, say), the newer
      // change stands.
      const undo = await update(user, async () => {
        const all = await grantRecords(user);
        const now = all.find(g => g.connection === id && sameSubject(g.subject, target));
        if (!now) return false;
        if (now.updated === before.made.updated) {
          await write(user, 'grants', [...all.filter(g => g !== now), ...(before.old ? [before.old] : [])]);
          return false;
        }
        if (scopeOf(now).join() !== granted.join()) return false;
        // Back to what it was, told to the helper: a grant in force is sent
        // again (unless it had this very scope), a refused one stays refused
        // with the helper told to drop what it was sent, none is revoked.
        const was = before.old?.state;
        if (was?.kind === 'granted' && was.scope.join() === granted.join()) return false;
        await putGrant(user, id, target, !was || was.kind === 'revoking' ? { kind: 'revoking' } : was.kind === 'refused' ? { ...was, revoking: true } : granting(was.scope));
        return true;
      });
      if (undo) { changed(user); void syncNow(user, id); }
      throw e;
    }
    const c: RelayConnection = { id, peer, route, relay, name: reply.name, harness: reply.harness, paired: Date.now() };
    await update(user, async () => {
      const all = await connections(user);
      // Paired again: still connected since it first was.
      c.paired = all.find(p => p.id === c.id)?.paired ?? c.paired;
      await write(user, 'connections', [...all.filter(p => p.id !== c.id), c]);
      // Paired just now: its agent made the link this browser redeemed.
      await write(user, seenKey(c.id), Date.now());
      if (code8) await write(user, 'requests', (await pendingRequests(user)).filter(r => r.req !== code8));
    });
    changed(user);
    await reconcile(user, c.id, reply);
    return c;
  },
  /** The agent's presence and its computer's own record of the grants (revoking there any this browser doesn't hold). */
  status: async (connection: string): Promise<{ online: boolean; presence: RelayPresence; grants: HelperGrant[] }> => {
    const user = await currentUser();
    const reply = await call<{ online: boolean; presence: RelayPresence; grants: HelperGrant[] }>(user, connection, { op: 'status' });
    await reconcile(user, connection, reply);
    return { online: reply.online, presence: reply.presence, grants: reply.grants };
  },
  /** This browser's grants, on `subject` or every one, granted or refused by the agent's computer (`status`). */
  grants: async (subject?: SubjectKey): Promise<RelayGrant[]> => {
    const user = await currentUser();
    const ids = new Set((await connections(user)).map(c => c.id));
    return (await liveGrants(user)).filter(g => ids.has(g.connection) && (!subject || sameSubject(g.subject, subject)));
  },
  /** Grants `connection` `subject` with `scope` (replacing an earlier scope) here at once, then on the agent's computer. */
  grant: async (connection: string, subject: SubjectKey, scope: RelayCategory[]): Promise<void> => {
    const user = await currentUser();
    const target = grantable(subject), granted = checkScope(scope, kindOf(target));
    await update(user, async () => {
      if (!(await connections(user)).some(c => c.id === connection)) throw new Error('Connect this browser to your agent first');
      const others = (await liveGrants(user)).filter(g => g.connection === connection && !sameSubject(g.subject, target));
      if (others.length >= MAX_GRANTS) throw new Error(`An agent can read at most ${MAX_GRANTS} subjects. Revoke one first.`);
      await putGrant(user, connection, target, granting(granted));
    });
    changed(user);
    void syncNow(user, connection);
  },
  /** Revokes `connection`'s grant on `subject`: this browser stops answering for it at once, and tells the agent's computer until it acknowledges. */
  revokeGrant: async (connection: string, subject: SubjectKey): Promise<void> => {
    const user = await currentUser();
    // Any subject, even one this Changeling would not grant now: revoking takes away whatever there is.
    const target = readKey(subject);
    if (!target) throw new Error('Not a subject.');
    await update(user, async () => {
      if (!(await connections(user)).some(c => c.id === connection)) throw new Error('Connect this browser to your agent first');
      await putGrant(user, connection, target, { kind: 'revoking' });
    });
    changed(user);
    void syncNow(user, connection);
  },
  /**
   * Tells the agent's computer what the history its grant request names by
   * a locator resolves to (`subject`): the request is then for that key,
   * which the person grants as any other. Its computer refuses a key the
   * locator cannot resolve to (relaySubject.ts `resolvesTo`).
   */
  resolve: async (connection: string, requestId: string, subject: SubjectKey): Promise<void> => {
    const user = await currentUser();
    await call(user, connection, { op: 'resolve', request: requestId, subject: grantable(subject) });
  },
  /** Declines an agent's grant request. */
  decline: async (connection: string, requestId: string): Promise<void> => {
    const user = await currentUser();
    await call(user, connection, { op: 'decline', request: requestId });
  },
  /** Records a request for a new agent to be granted `subject`, for ten minutes. */
  createRequest: async (subject: SubjectKey, scope: RelayCategory[]): Promise<PendingRequest> => {
    const user = await currentUser();
    const target = grantable(subject), granted = checkScope(scope, kindOf(target));
    const plain = [...crypto.getRandomValues(new Uint8Array(8))].map(b => BASE32[b & 31]).join('');
    const r: PendingRequest = { req: requestCode(plain)!, subject: target, scope: [...granted], created: Date.now() };
    await update(user, async () => write(user, 'requests', [...(await pendingRequests(user)).slice(-19), r]));
    return r;
  },
  /** The unexpired request `req` started in Changeling (with or without its dash, in any case). */
  findRequest: async (req: string): Promise<PendingRequest | null> => {
    const code8 = requestCode(req);
    if (!code8) return null;
    return (await pendingRequests(await currentUser())).find(r => r.req === code8) ?? null;
  },
  /**
   * Calls `cb` with each grant request an agent makes while this tab is
   * open (again when it changes), and `onGone` once it is settled, by this
   * browser or the agent's computer, or expired.
   */
  onGrantRequest: (cb: (r: GrantRequest) => void, onGone?: (id: string, connection: string) => void): (() => void) => {
    const offs = [listen(requestListeners, cb), ...(onGone ? [listen(goneListeners, onGone)] : [])];
    return () => { for (const off of offs) off(); };
  },
  /** Why this tab stopped starting the relay worker by itself (it kept stopping), or null while it doesn't. */
  workerDown: (): string | null => down,
  /**
   * Starts the relay worker again (the person's Try again once `workerDown`):
   * a request that needs no agent, which resolves once the new worker answers
   * it; `workerDown` stays until then. A worker that ends before it answers
   * leaves it down, as it was, and this fails with REVIVE_FAILED.
   */
  revive: async (): Promise<void> => {
    try { await post<null>({ op: 'ping' }, true); } catch (e) { throw down !== null ? new Error(REVIVE_FAILED) : e; }
  },
  /** Calls `cb` with `workerDown`'s answer each time it changes. */
  onWorkerDown: (cb: (why: string | null) => void): (() => void) => { downListeners.add(cb); return () => void downListeners.delete(cb); },
  /** Calls `cb` when the grants change, in this tab or another. */
  onGrantsChanged: (cb: () => void): (() => void) => listen(grantListeners, cb),
  /**
   * Calls `cb` when the worker hears from a pairing's agent, with when.
   * Heard only while this tab is attached (by its other listeners): it
   * doesn't attach the tab by itself.
   */
  onSeen: (cb: (connection: string, at: number) => void): (() => void) => { seenListeners.add(cb); return () => void seenListeners.delete(cb); },
  /** Answers agents' queries from this tab, within the grants; the last registered `answer` is used. */
  serveQueries: (answer: Answerer): (() => void) => listen(answerers, answer),
  /** Disconnects a pairing. Its grants stop here at once and, should the computer be unreachable, are revoked there when it next is. */
  revoke: async (id: string) => {
    const user = await currentUser();
    await update(user, async () => {
      for (const g of await liveGrants(user)) if (g.connection === id) await putGrant(user, id, g.subject, { kind: 'revoking' });
    });
    changed(user);
    await call(user, id, { op: 'revoke' });
    await removeLocally(user, id);
  },
  forget,
  resetIdentity,
  clearForSignOut,
  tasks: async (id: string, topic?: string) => {
    const user = await currentUser();
    const pending = (await outbox(user)).filter(p => p.connection === id && (!topic || p.task.topic === topic)).map(p => p.task);
    const key = `history:${id}:${topic ?? ""}`;
    try {
      const result = await call<{ tasks: RelayTask[]; online: boolean; presence: RelayPresence }>(user, id, { op: 'tasks', topic });
      await update(user, async () => {
        if ((await connections(user)).some(c => c.id === id)) await write(user, key, result.tasks);
        await write(user, 'outbox', (await outbox(user)).filter(p => p.connection !== id || !result.tasks.some(t => t.id === p.task.id)));
      });
      return { ...result, reachable: true, tasks: await withAsked(user, id, topic, [...result.tasks, ...pending.filter(t => !result.tasks.some(r => r.id === t.id))]) };
    } catch(e) { const known = await history(user, key); return { tasks: await withAsked(user, id, topic, [...known, ...pending.filter(t => !known.some(h => h.id === t.id))]), online: false, reachable: false, presence: 'offline' as RelayPresence, error: message(e) }; }
  },
  /** Sends `body` (relayTask.ts) to `id`'s agent on `topic`, recorded first so a lost acknowledgement can be retried. */
  send: async (id: string, topic: string, body: TaskBody) => {
    const user = await currentUser();
    const { kind, payload } = body;
    if (!isTopic(topic)) throw new Error('Not a topic.');
    const task = await update(user, async () => {
      const pending = await outbox(user);
      const old = pending.find(p => p.connection === id && p.task.topic === topic);
      if (old) {
        if (old.task.kind !== kind || JSON.stringify(old.task.payload) !== JSON.stringify(payload)) throw new Error('An earlier delivery is unconfirmed. Retry that request before sending different instructions.');
        return old.task;
      }
      if (pending.length >= 100) throw new Error('Too many unconfirmed requests. Reconnect your agent before sending more.');
      if (new TextEncoder().encode(JSON.stringify(payload)).byteLength > 65536) throw new Error('This is too large to send in one request.');
      const task: RelayTask = { id: crypto.randomUUID(), topic, kind, payload: payload as unknown as RelayTask['payload'], status: 'unconfirmed', result: null, lease_until: null };
      // Recorded with the outbox, before delivery: a lost acknowledgement still leaves a task to find.
      const sent = (await sentRecords(user)).filter(r => r.connection !== id || r.topic !== topic);
      await write(user, 'sent', [...sent, { connection: id, topic }].slice(-SENT_MAX));
      await write(user, askedKey(id, topic), [...(await read<Asked[]>(user, askedKey(id, topic)) ?? []), { task: task.id, text: payload.instruction }].slice(-ASKED_MAX));
      if (body.kind === RANGE_TASK) {
        const p = body.payload;
        const range: SentRange = { task: task.id, topic, subject: p.subject, path: p.path, side: p.side, blob: p.blob, start_line: p.start_line, end_line: p.end_line, commit: p.commit };
        await write(user, rangesKey(id), [...(await read<SentRange[]>(user, rangesKey(id)) ?? []), range].slice(-RANGES_MAX));
      }
      await write(user, 'outbox', [...pending, { connection: id, task }]); return task;
    });
    return deliver(id, task);
  },
  retry: (id: string, task: RelayTask) => deliver(id, task),
  /** The connections this browser has sent `topic` to, in the order sent. */
  sentTo: async (topic: string): Promise<string[]> => (await sentRecords(await currentUser())).filter(r => r.topic === topic).map(r => r.connection),
  cancel: async (id: string, task: string) => call(await currentUser(), id, { op: 'cancel', id: task }),
  /** Tells `cb` of each serve's word of a pairing's newest finished range task (a `results` cursor). Returns the stop. */
  onResults: (cb: (connection: string, after: string) => void): (() => void) => {
    resultListeners.add(cb);
    return () => void resultListeners.delete(cb);
  },
  /** The range tasks this browser sent pairing `id`, with what each was sent with. */
  sentRanges: async (id: string): Promise<SentRange[]> => (await read<SentRange[]>(await currentUser(), rangesKey(id))) ?? [],
  /** A page of pairing `id`'s finished range tasks' results after `after` (null: from the first), oldest first. */
  results: async (id: string, after: string | null, limit: number): Promise<{ results: RelayResult[]; after: string | null; more: boolean }> =>
    call(await currentUser(), id, { op: 'results', after, limit }),
  /** Where this browser's reading of pairing `id`'s results has got to (null: nowhere yet). */
  resultsCursor: async (id: string): Promise<string | null> => (await read<string>(await currentUser(), cursorKey(id))) ?? null,
  /** Moves that on to `after`, never back (two tabs may read at once). */
  advanceResults: async (id: string, after: string): Promise<void> => {
    const user = await currentUser();
    await update(user, async () => {
      if (!(await connections(user)).some((c) => c.id === id)) return;
      if (laterCursor(after, (await read<string>(user, cursorKey(id))) ?? null)) await write(user, cursorKey(id), after);
    });
  },
};
